import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Attach JWT token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nexus_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for auth endpoints
      if (!error.config.url.includes('/auth/')) {
        localStorage.removeItem('nexus_token');
        localStorage.removeItem('nexus_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (email, password) => {
  const { data } = await api.post('/auth/login', { email, password });
  return data;
};

export const register = async (email, password, name, role = 'END_USER') => {
  const { data } = await api.post('/auth/register', { email, password, name, role });
  return data;
};

export const fetchMe = async () => {
  const { data } = await api.get('/auth/me');
  return data;
};

// Dashboard
export const fetchKpis = async () => { const { data } = await api.get('/kpis'); return data; };
export const fetchAgentsStatus = async () => { const { data } = await api.get('/agents/status'); return data; };
export const fetchActivity = async (limit = 50) => { const { data } = await api.get(`/activity?limit=${limit}`); return data; };
export const fetchNavBadges = async () => { const { data } = await api.get('/nav-badges'); return data; };
export const fetchSlaBurn = async () => { const { data } = await api.get('/sla-burn'); return data; };
export const fetchIncidentHeatmap = async () => { const { data } = await api.get('/incident-heatmap'); return data; };
export const fetchMttrTrend = async () => { const { data } = await api.get('/mttr-trend'); return data; };

// Tickets
export const fetchTickets = async (params = {}) => { const { data } = await api.get('/tickets', { params }); return data; };
export const fetchTicket = async (ticketId) => { const { data } = await api.get(`/tickets/${ticketId}`); return data; };
export const updateTicket = async (ticketId, updates) => { const { data } = await api.patch(`/tickets/${ticketId}`, updates); return data; };
export const createTicket = async (ticket) => { const { data } = await api.post('/tickets', ticket); return data; };
export const fetchTicketActions = async (ticketId) => { const { data } = await api.get(`/tickets/${ticketId}/actions`); return data; };
export const fetchTicketTimeline = async (ticketId) => { const { data } = await api.get(`/tickets/${ticketId}/timeline`); return data; };

// Agents
export const fetchAgentActions = async (agentName, limit = 50) => { const { data } = await api.get(`/agents/${agentName}/actions?limit=${limit}`); return data; };

// Knowledge
export const fetchKnowledge = async (search = '') => { const params = search ? { search } : {}; const { data } = await api.get('/knowledge', { params }); return data; };
export const fetchKnowledgeArticle = async (articleId) => { const { data } = await api.get(`/knowledge/${articleId}`); return data; };
export const submitKnowledgeFeedback = async (articleId, helpful) => { const { data } = await api.post(`/knowledge/${articleId}/feedback`, { helpful }); return data; };

// Assets & Topology
export const fetchAssets = async (params = {}) => { const { data } = await api.get('/assets', { params }); return data; };
export const fetchAsset = async (assetId) => { const { data } = await api.get(`/assets/${assetId}`); return data; };
export const fetchAssetRelationships = async (assetId) => { const { data } = await api.get(`/assets/${assetId}/relationships`); return data; };
export const fetchAssetImpact = async (assetId) => { const { data } = await api.get(`/assets/${assetId}/impact`); return data; };
export const fetchTopology = async () => { const { data } = await api.get('/topology'); return data; };

// Changes & Risk
export const fetchChanges = async (params = {}) => { const { data } = await api.get('/changes', { params }); return data; };
export const fetchChangeRisk = async (changeId) => { const { data } = await api.get(`/changes/${changeId}/risk`); return data; };

// Other modules
export const fetchProblems = async () => { const { data } = await api.get('/problems'); return data; };
export const fetchRequests = async () => { const { data } = await api.get('/requests'); return data; };
export const fetchAudit = async (limit = 100) => { const { data } = await api.get(`/audit?limit=${limit}`); return data; };

// Bulk actions
export const bulkTicketAction = async (ticketIds, action, value = null) => {
  const { data } = await api.post('/tickets/bulk', { ticket_ids: ticketIds, action, value });
  return data;
};

// Enhanced Analytics
export const fetchResolutionTrend = async () => { const { data } = await api.get('/analytics/resolution-trend'); return data; };
export const fetchAgentPerformance = async () => { const { data } = await api.get('/analytics/agent-performance'); return data; };
export const fetchIncidentVolume = async () => { const { data } = await api.get('/analytics/incident-volume'); return data; };
export const fetchCostAvoidance = async () => { const { data } = await api.get('/analytics/cost-avoidance'); return data; };

// SLA Predictions
export const fetchSlaPredictions = async () => { const { data } = await api.get('/sla/predictions'); return data; };

// Change Calendar
export const fetchChangeCalendar = async () => { const { data } = await api.get('/changes/calendar'); return data; };

// CONCIERGE Chat
export const sendConciergeMessage = async (message, sessionId) => {
  const { data } = await api.post('/concierge/message', { message, session_id: sessionId });
  return data;
};
export const fetchConciergeHistory = async (sessionId) => {
  const { data } = await api.get(`/concierge/history?session_id=${sessionId}`);
  return data;
};

// COMPLIANCE-OPS
export const fetchAuditTrail = async (limit = 100) => { const { data } = await api.get(`/audit-trail?limit=${limit}`); return data; };
export const verifyAuditChain = async (startSeq = 1, endSeq = null) => { const { data } = await api.post('/audit-trail/verify', { start_sequence: startSeq, end_sequence: endSeq }); return data; };
export const generateReport = async (framework, periodDays = 30) => { const { data } = await api.post('/reports/generate', { framework, period_days: periodDays }); return data; };
export const fetchReports = async () => { const { data } = await api.get('/reports'); return data; };
export const fetchCapacityInsights = async () => { const { data } = await api.get('/analytics/capacity-insights'); return data; };
export const fetchPublicStatus = async () => { const { data } = await api.get('/status'); return data; };
export const fetchSecurityIncidents = async () => { const { data } = await api.get('/security-incidents'); return data; };

export default api;
